import { FedopsLogger } from '@wix/fedops-logger';
import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  AppInstance,
  RevalidateAppInstance,
} from '@wix/cashier-common/dist/src/types/AppInstance';
import {
  logInstantPaymentModalClosed,
  logInstantPaymentModalOpened,
  convertToBiParams,
  InstantPaymentModalClosedReason,
} from './biUtils';
import { useTimeout } from '../../../utils/hooks';
import { getTranslationsByLang } from '../../../utils/i18n';
import { selectDeviceType } from '../../../redux/selectors';
import { InteractionName } from '../../../types/InteractionName';
import { FramedModal } from '../FramedModal';
import { FramedModalApi, FramedModalProps } from '../FramedModal/controller';
import { instantPaymentModalConfig } from './constants';
import {
  InstantPaymentDetails,
  InstantPaymentMessageFromFrame,
  InstantPaymentMessageToFrame,
  InstantPaymentMessageType,
} from './types';
import { AsyncPaymentResult } from '../../../sdk/types/AsyncPaymentResult';

export interface InstantPaymentModalProps {
  publicBaseUrl: string;
  appInstance: AppInstance;
  locale: string;
  userId: string;
  paymentDetails: InstantPaymentDetails;
  fedopsLogger: FedopsLogger<InteractionName>;

  onComplete(paymentResult?: AsyncPaymentResult): void;
  onError(error?: any): void;
  onManualClose(): void;
  onTimeout(): void;
  revalidateAppInstance?: RevalidateAppInstance;
}

export const InstantPaymentModalProvider: React.FC<
  InstantPaymentModalProps & {
    children(
      props: FramedModalProps
    ): React.ReactComponentElement<typeof FramedModal>;
  }
> = (props) => {
  const deviceType = useSelector(selectDeviceType);
  const apiRef = useRef<FramedModalApi>();
  const handleApiInit = useCallback((api: FramedModalApi) => {
    apiRef.current = api;
  }, []);
  const handleReadyTimeout = useCallback(() => {
    props.onError(new Error('Failed to initialize Instant Payment modal'));
  }, [props.onError]);

  const clearTimeout = useTimeout({
    onTimeout: handleReadyTimeout,
    timeout: instantPaymentModalConfig.maxReadyTimeout,
  });

  const handleMessageFromProxyPage = useCallback(
    (data: InstantPaymentMessageFromFrame) => {
      switch (data.type) {
        case InstantPaymentMessageType.Ready:
          clearTimeout();
          logInstantPaymentModalOpened(convertToBiParams(props.paymentDetails));
          apiRef.current?.sendTransitiveMessageToFrame<InstantPaymentMessageToFrame>(
            {
              type: InstantPaymentMessageType.Init,
              payload: {
                deviceType,
                locale: props.locale,
                translations: getTranslationsByLang(props.locale),
                appInstance: props.appInstance,
                paymentDetails: props.paymentDetails,
              },
            } as InstantPaymentMessageToFrame
          );
          break;
        case InstantPaymentMessageType.PaymentCompleted:
          logInstantPaymentModalClosed({
            ...convertToBiParams(props.paymentDetails),
            reason: InstantPaymentModalClosedReason.Success,
          });
          props.onComplete(data.payload);
          break;
        case InstantPaymentMessageType.PaymentCancelled:
          logInstantPaymentModalClosed({
            ...convertToBiParams(props.paymentDetails),
            reason: InstantPaymentModalClosedReason.Decline,
          });
          props.onManualClose();
          break;
        case InstantPaymentMessageType.Error:
          props.onError(data.error);
          break;
        case InstantPaymentMessageType.PaymentTimeout:
          logInstantPaymentModalClosed({
            ...convertToBiParams(props.paymentDetails),
            reason: InstantPaymentModalClosedReason.Timeout,
          });
          props.onTimeout();
          break;
        default:
      }
    },
    [props.onComplete, props.onManualClose, props.onError, props.onTimeout]
  );

  const handleFrameMount = useCallback(() => {
    props.fedopsLogger.interactionEnded(
      InteractionName.INSTANT_PAYMENT_MODAL_MOUNT
    );
  }, []);

  return props.children({
    modalName: instantPaymentModalConfig.name,
    url: instantPaymentModalConfig.pageUrl,
    locale: props.locale,
    width: instantPaymentModalConfig.size,
    height: instantPaymentModalConfig.size,
    publicBaseUrl: props.publicBaseUrl,
    userId: props.userId,
    fedopsLogger: props.fedopsLogger,
    onClose: props.onManualClose,
    onReadyTimeout: handleReadyTimeout,
    onApiInit: handleApiInit,
    onTransitiveMessageFromFrame: handleMessageFromProxyPage,
    onFrameMount: handleFrameMount,
    revalidateAppInstance: props.revalidateAppInstance,
  });
};
InstantPaymentModalProvider.displayName = 'InstantPaymentModalProvider';

export const InstantPaymentModal: React.FC<InstantPaymentModalProps> = (
  props
) => (
  <InstantPaymentModalProvider {...props}>
    {(renderProps) => <FramedModal {...renderProps} />}
  </InstantPaymentModalProvider>
);
InstantPaymentModal.displayName = 'InstantPaymentModal';
